import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Eye, EyeOff, ArrowRight, X, CheckCircle } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Logo = () => (
  <a href="/" className="inline-flex items-baseline cursor-pointer hover:opacity-80 transition-opacity">
    <span className="text-xl font-black text-[#0F172A] font-['Outfit'] tracking-tight">ORDO AI</span>
  </a>
);

const Signup = () => {
  const navigate = useNavigate();
  const [loading, setLoading]               = useState(false);
  const [googleLoading, setGoogleLoading]   = useState(false);
  const [showPassword, setShowPassword]     = useState(false);
  const [acceptedTerms, setAcceptedTerms]   = useState(false);
  const [policyModal, setPolicyModal]       = useState(null);
  const [formData, setFormData] = useState({
    name: "", email: "", password: "", confirmPassword: "",
  });

  const update = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  // ── Email signup ───────────────────────────────────────────────────
  const handleSignup = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.password) {
      toast.error("Please fill in all fields"); return;
    }
    if (formData.password !== formData.confirmPassword) {
      toast.error("Passwords do not match"); return;
    }
    if (formData.password.length < 6) {
      toast.error("Password must be at least 6 characters"); return;
    }
    if (!acceptedTerms) {
      toast.error("Please accept the Terms & Conditions"); return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          name:             formData.name,
          email:            formData.email,
          password:         formData.password,
          confirm_password: formData.confirmPassword,
        }),
      });
      const data = await res.json();
      if (res.ok) {
        if (data.session_id) localStorage.setItem("session_id", data.session_id);
        toast.success("Account created!");
        navigate("/onboarding");  // ← always go to onboarding
      } else {
        toast.error(data.detail || "Signup failed");
      }
    } catch {
      toast.error("Connection error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // ── Google signup — uses backend endpoint to get correct redirect_uri ──
  const handleGoogleSignup = async () => {
    setGoogleLoading(true);
    try {
      const res = await fetch(`${API}/auth/google/url`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          redirect_uri: "https://rankfirst-production.up.railway.app/api/auth/google/callback",
        }),
      });
      if (res.ok) {
        const data = await res.json();
        window.location.href = data.auth_url;
      } else {
        toast.error("Failed to initialize Google signup");
        setGoogleLoading(false);
      }
    } catch {
      toast.error("Error starting Google signup");
      setGoogleLoading(false);
    }
  };

  // Get plan name for display
  const planIntent = localStorage.getItem("plan_intent");
  const planLabel  = planIntent === "growth" ? "Growth Plan" : planIntent === "basic" ? "Basic Plan" : null;

  return (
    <div className="min-h-screen bg-[#F8FAFC] font-['Outfit'] flex flex-col">

      {/* NAV */}
      <nav className="bg-white border-b border-slate-200 h-16 flex items-center px-6">
        <div className="max-w-7xl mx-auto w-full flex items-center justify-between">
          <Logo />
          {planLabel && (
            <div className={`text-xs font-bold px-3 py-1.5 rounded-full ${
              planIntent === "growth" ? "bg-[#1E40AF] text-white" : "bg-slate-100 text-slate-700"
            }`}>
              {planLabel} selected
            </div>
          )}
        </div>
      </nav>

      {/* MAIN */}
      <div className="flex-1 flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-md">

          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="h-1.5 bg-[#10B981]" />
            <div className="p-8">
              <div className="text-center mb-6">
                <h1 className="text-2xl font-black text-[#0F172A] font-['Outfit'] mb-1">Create your account</h1>
                <p className="text-slate-500 text-sm">3 days free trial · No setup fees · Cancel anytime</p>
              </div>

              {/* Google */}
              <button
                onClick={handleGoogleSignup}
                disabled={googleLoading}
                className="w-full flex items-center justify-center gap-3 h-12 border border-slate-200 rounded-xl text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors mb-5 disabled:opacity-60">
                {googleLoading
                  ? <div className="w-4 h-4 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin" />
                  : <svg className="w-5 h-5" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                }
                Continue with Google
              </button>

              <div className="flex items-center gap-3 mb-5">
                <div className="flex-1 h-px bg-slate-200" />
                <span className="text-xs text-slate-400 font-medium">or</span>
                <div className="flex-1 h-px bg-slate-200" />
              </div>

              {/* Form */}
              <form onSubmit={handleSignup} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#0F172A] mb-1.5">Full Name</label>
                  <input
                    type="text" name="name"
                    value={formData.name} onChange={update}
                    placeholder="John Doe"
                    className="w-full h-12 px-4 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:border-[#1E40AF] focus:outline-none focus:bg-white transition-colors text-black placeholder-slate-400"
                    autoFocus
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#0F172A] mb-1.5">Email</label>
                  <input
                    type="email" name="email"
                    value={formData.email} onChange={update}
                    placeholder="you@example.com"
                    className="w-full h-12 px-4 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:border-[#1E40AF] focus:outline-none focus:bg-white transition-colors text-black placeholder-slate-400"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#0F172A] mb-1.5">Password</label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"} name="password"
                      value={formData.password} onChange={update}
                      placeholder="••••••••"
                      className="w-full h-12 px-4 pr-12 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:border-[#1E40AF] focus:outline-none focus:bg-white transition-colors text-black placeholder-slate-400"
                    />
                    <button type="button" onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#0F172A] mb-1.5">Confirm Password</label>
                  <input
                    type={showPassword ? "text" : "password"} name="confirmPassword"
                    value={formData.confirmPassword} onChange={update}
                    placeholder="••••••••"
                    className="w-full h-12 px-4 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:border-[#1E40AF] focus:outline-none focus:bg-white transition-colors text-black placeholder-slate-400"
                  />
                </div>

                {/* Terms */}
                <div className="flex items-start gap-3 pt-1">
                  <button
                    type="button"
                    onClick={() => setAcceptedTerms(!acceptedTerms)}
                    className={`mt-0.5 w-5 h-5 rounded flex-shrink-0 border-2 flex items-center justify-center transition-colors ${
                      acceptedTerms ? "bg-[#1E40AF] border-[#1E40AF]" : "border-slate-300 bg-white"
                    }`}>
                    {acceptedTerms && <CheckCircle className="w-3 h-3 text-white" />}
                  </button>
                  <label className="text-sm text-slate-600 cursor-pointer" onClick={() => setAcceptedTerms(!acceptedTerms)}>
                    I agree to the{" "}
                    <button type="button" onClick={(e) => { e.stopPropagation(); setPolicyModal("terms"); }}
                      className="text-[#1E40AF] hover:underline font-medium">Terms of Service</button>{" "}
                    and{" "}
                    <button type="button" onClick={(e) => { e.stopPropagation(); setPolicyModal("privacy"); }}
                      className="text-[#1E40AF] hover:underline font-medium">Privacy Policy</button>
                  </label>
                </div>

                <button type="submit" disabled={loading}
                  className="w-full bg-[#10B981] hover:bg-[#059669] text-white font-bold h-12 rounded-xl text-sm flex items-center justify-center gap-2 transition-colors disabled:opacity-60">
                  {loading
                    ? <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                    : <>Create Account <ArrowRight className="w-4 h-4" /></>
                  }
                </button>
              </form>
            </div>
          </div>

          <p className="text-center text-sm text-slate-500 mt-5">
            Already have an account?{" "}
            <a href="/login" className="text-[#1E40AF] font-semibold hover:underline">Log in</a>
          </p>
        </div>
      </div>

      {/* FOOTER */}
      <footer className="bg-white border-t border-slate-200 py-5">
        <div className="max-w-7xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <Logo />
          <p className="text-xs text-slate-400">© 2026 ORDO AI LLC</p>
        </div>
      </footer>

      {/* POLICY MODALS */}
      {policyModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4"
          style={{ background: "rgba(0,0,0,0.5)", backdropFilter: "blur(4px)" }}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <h3 className="font-bold text-[#0F172A] font-['Outfit']">
                {policyModal === "terms" ? "Terms of Service" : "Privacy Policy"}
              </h3>
              <button onClick={() => setPolicyModal(null)} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-6 py-5 overflow-y-auto flex-1">
              <p className="text-xs text-slate-600 leading-relaxed">
                {policyModal === "terms"
                  ? "By creating an account you agree to ORDO AI's Terms of Service. Each subscription is tied to a single business. Payments are final and non-refundable after the 3-day free trial. You authorize ORDO AI to act as your representative in outreach and optimization activities. Governed by the laws of Florida. Contact: support@theordo.io"
                  : "ORDO AI collects your business information to provide AI visibility scanning, GEO and SEO optimization, and outreach services. We do not sell your data. Contact: support@theordo.io"
                }
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Signup;
